import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Tag, Store, Loader2, ChevronDown, ChevronUp,
  Search, DollarSign, ShoppingCart, CheckCircle2, Heart,
  Newspaper, Bell, ArrowDown, TrendingDown, Users, X, ExternalLink, Wallet, ShoppingBag, Sparkles,
  Clock, RefreshCw, Info, ThumbsDown, ZoomIn, Lock, Crown, MapPin
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { WeeklyAdsTab } from "../components/WeeklyAdsTab";
import CommunityDealsTab from "../components/CommunityDealsTab";
import { useListContext } from "../contexts/ListContext";
import { useAuth } from "../contexts/AuthContext";
import { useWeeklyAdsAccess } from "../contexts/WeeklyAdsAccessContext";
import { useGuest } from "../contexts/GuestContext";
import { useDealLocation } from "../hooks/useDealLocation";
import GuestZipPrompt from "../components/GuestZipPrompt";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Product Detail Modal ─────────────────────────────────────────────────

const ProductDetailModal = ({ product, onClose, groceryItemName, onSelectForList }) => {
  if (!product) return null;

  const canSelect = !!groceryItemName && !!onSelectForList && product.price != null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="product-detail-overlay">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="product-detail-modal">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 text-sm truncate pr-2">{product.name}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full flex-shrink-0" data-testid="close-product-detail">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {/* Product Image */}
        <div className="bg-gray-50 flex items-center justify-center p-6">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="max-w-full max-h-48 object-contain rounded-lg" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="w-32 h-32 bg-amber-50 rounded-xl flex items-center justify-center">
              <Tag className="h-10 w-10 text-amber-300" />
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="p-4 space-y-3">
          <div>
            <h4 className="font-bold text-lg text-gray-900">{product.name}</h4>
            <div className="flex items-center gap-1.5 mt-1">
              <Store className="h-3.5 w-3.5 text-gray-500" />
              <span className="text-sm text-gray-600 font-medium">{product.store}</span>
            </div>
          </div>

          {/* Price Section */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-baseline gap-3">
              {product.price != null ? (
                <span className="text-3xl font-bold text-green-700">${product.price.toFixed(2)}</span>
              ) : (
                <span className="text-lg text-gray-500">Price not listed</span>
              )}
              {product.original_price != null && product.original_price > (product.price || 0) && (
                <span className="text-lg text-gray-400 line-through">${product.original_price.toFixed(2)}</span>
              )}
            </div>
            {product.original_price != null && product.original_price > (product.price || 0) && (
              <div className="mt-1">
                <span className="text-sm font-semibold text-green-600">
                  Save ${(product.original_price - product.price).toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {/* Category */}
          {product.category && (
            <div className="flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-sm text-gray-500">{product.category}</span>
            </div>
          )}

          {/* Validity */}
          {(product.valid_from || product.valid_to) && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 font-medium">Sale Period</p>
              <p className="text-sm text-gray-700">
                {product.valid_from && new Date(product.valid_from).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {product.valid_from && product.valid_to && " — "}
                {product.valid_to && new Date(product.valid_to).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            </div>
          )}

          {/* Use This Button */}
          {canSelect && (
            <Button
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold"
              onClick={() => { onSelectForList(product); onClose(); }}
              data-testid="use-this-product-btn"
            >
              <ShoppingBag className="h-4 w-4 mr-2" />
              Use This for "{groceryItemName}"
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Text-Only Product Info Modal (Price Compare + Budget Friendly) ──────
// Renders enriched flyer product details WITHOUT images. Used for the deal
// tabs where we intentionally strip imagery for legal safety.

const TextOnlyProductInfoModal = ({ open, product, store, storeContext, groceryContext, onClose, onAdd }) => {
  if (!open || !product) return null;

  // `product` may be either the raw enriched `details` payload OR a store
  // entry with a nested `details` field. Support both.
  const d = product.details || product;
  const displayName = d.base_name || d.name || product.name || "Item";
  const storeName = store || product.store || "";
  const price = d.price ?? product.price ?? null;
  const originalPrice = d.original_price ?? product.original_price ?? null;
  const savings = originalPrice != null && price != null && originalPrice > price
    ? Number((originalPrice - price).toFixed(2))
    : null;
  const savingsPct = d.savings_pct ?? null;
  const brand = d.brand || null;
  const category = d.category || product.category || null;
  const size = d.size || product.size_raw || null;
  const variants = Array.isArray(d.variants) ? d.variants : null;
  const discountText = d.discount_text || null;
  const validFrom = d.valid_from || product.valid_from || null;
  const validTo = d.valid_to || product.valid_to || null;
  const daysRemaining = d.days_remaining ?? null;
  const unitPrice = product.unit_price ?? null;
  const unitPriceLabel = product.unit_price_label || "";

  const canAdd = !!onAdd && price != null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="product-info-overlay"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="product-info-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-100 gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold mb-1">Deal Details</p>
            <h3 className="font-bold text-gray-900 text-base leading-snug" data-testid="product-info-name">
              {displayName}
            </h3>
            {brand && (
              <p className="text-xs text-gray-500 mt-0.5" data-testid="product-info-brand">by {brand}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full flex-shrink-0"
            data-testid="close-product-info"
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* Store */}
          {storeName && (
            <div className="flex items-center gap-1.5" data-testid="product-info-store">
              <Store className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700 font-semibold">{storeName}</span>
              {storeContext && (
                <Badge className="bg-green-100 text-green-700 text-[10px] ml-1">{storeContext}</Badge>
              )}
            </div>
          )}

          {/* Price + Savings */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-baseline gap-3 flex-wrap">
              {price != null ? (
                <span className="text-3xl font-bold text-green-700" data-testid="product-info-price">
                  ${price.toFixed(2)}
                </span>
              ) : (
                <span className="text-lg text-gray-500">Price not listed</span>
              )}
              {originalPrice != null && originalPrice > (price || 0) && (
                <span className="text-lg text-gray-400 line-through">${originalPrice.toFixed(2)}</span>
              )}
              {savingsPct && (
                <Badge className="bg-red-100 text-red-700 text-xs">-{savingsPct}%</Badge>
              )}
            </div>
            {savings != null && (
              <p className="mt-1.5 text-sm font-semibold text-green-600">
                Save ${savings.toFixed(2)}
              </p>
            )}
            {unitPrice && (
              <p className="mt-1 text-xs text-gray-500 font-mono">
                ${unitPrice.toFixed(2)}{unitPriceLabel}
              </p>
            )}
            {discountText && (
              <div className="mt-2 pt-2 border-t border-green-200/60">
                <div className="flex items-start gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-700 font-medium">{discountText}</span>
                </div>
              </div>
            )}
          </div>

          {/* Product Attributes Grid */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            {size && (
              <div className="bg-gray-50 rounded-lg p-2.5" data-testid="product-info-size">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-0.5">Size / Count</p>
                <p className="text-sm text-gray-800 font-medium">{size}</p>
              </div>
            )}
            {category && (
              <div className="bg-gray-50 rounded-lg p-2.5" data-testid="product-info-category">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-0.5">Category</p>
                <p className="text-sm text-gray-800 font-medium">{category}</p>
              </div>
            )}
            {brand && (
              <div className="bg-gray-50 rounded-lg p-2.5">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-0.5">Brand</p>
                <p className="text-sm text-gray-800 font-medium">{brand}</p>
              </div>
            )}
            {/* flyer page removed per user request — Feb 2026 */}
          </div>

          {/* Variants (combo listings from flyer) */}
          {variants && variants.length > 0 && (
            <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-3" data-testid="product-info-variants">
              <p className="text-[10px] uppercase tracking-wide text-amber-700 font-bold mb-1.5">
                Also available in this deal
              </p>
              <ul className="space-y-0.5">
                {variants.slice(0, 6).map((v, i) => (
                  <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                    <span className="text-amber-600 flex-shrink-0">•</span>
                    <span>{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Sale Period */}
          {(validFrom || validTo) && (
            <div className="bg-gray-50 rounded-lg p-3" data-testid="product-info-validity">
              <div className="flex items-center gap-1.5 mb-1">
                <Clock className="h-3.5 w-3.5 text-gray-500" />
                <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Sale Window</p>
              </div>
              <p className="text-sm text-gray-800">
                {validFrom && new Date(validFrom).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {validFrom && validTo && " — "}
                {validTo && new Date(validTo).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
              {daysRemaining != null && daysRemaining >= 0 && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {daysRemaining === 0 ? "Ends today" : daysRemaining === 1 ? "1 day left" : `${daysRemaining} days left`}
                </p>
              )}
            </div>
          )}

          {/* Legal disclaimer */}
          <p className="text-[10px] text-gray-400 italic text-center pt-1">
            Text-only details from public retailer price comparisons. Prices &amp; availability vary by store.
          </p>

          {/* Add to List */}
          {canAdd && (
            <Button
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold"
              onClick={() => { onAdd(); onClose(); }}
              data-testid="product-info-add-btn"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              {groceryContext ? `Use for "${groceryContext}"` : "Add to my grocery list"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Grocery List Saver Tab ──────────────────────────────────────────────

const GroceryListSaverTab = ({ highlightItem = "", onHighlightHandled = () => {}, zip = "", radius = 10 }) => {
  const { currentList, replaceItemWithDeal, batchReplaceItemsWithDeals } = useListContext();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [totalMatches, setTotalMatches] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedGroceryItem, setSelectedGroceryItem] = useState(null);
  const [replacedItems, setReplacedItems] = useState(new Set());
  const [flashItem, setFlashItem] = useState(null); // short-lived green pulse on deep-linked cards
  const [highlightQueue, setHighlightQueue] = useState([]); // ordered list of alerted grocery items from deep-link
  const [highlightIndex, setHighlightIndex] = useState(0);
  const alertCardRefs = useRef({});

  const scrollAndFlash = (groceryName) => {
    setExpandedItem(groceryName);
    setFlashItem(groceryName);
    // Wait for the card to render before scrolling
    setTimeout(() => {
      const node = alertCardRefs.current[groceryName];
      if (node && typeof node.scrollIntoView === "function") {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      // Clear the flash after the pulse animation finishes
      setTimeout(() => setFlashItem(null), 2200);
    }, 80);
  };

  const handleNextHighlight = () => {
    if (highlightQueue.length < 2) return;
    const nextIdx = (highlightIndex + 1) % highlightQueue.length;
    setHighlightIndex(nextIdx);
    scrollAndFlash(highlightQueue[nextIdx]);
  };

  const groceryItems = (currentList?.items || [])
    .filter((i) => !i.completed)
    .map((i) => i.name);

  const scanForDeals = async () => {
    if (groceryItems.length === 0) return;
    setLoading(true);
    try {
      const url = new URL(`${BACKEND_URL}/api/weekly-ads/deal-alerts`);
      url.searchParams.set("items", groceryItems.join(","));
      if (zip) url.searchParams.set("zip", zip);
      url.searchParams.set("radius", String(radius));
      const res = await fetch(url.toString());
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
        setTotalMatches(data.total_matches || 0);
      }
    } catch (e) {
      console.error("Deal scan failed:", e);
    } finally {
      setLoading(false);
      setScanned(true);
    }
  };

  useEffect(() => {
    if (groceryItems.length > 0) {
      scanForDeals();
    }
  }, [zip, radius]); // eslint-disable-line

  // Deep-link: when arriving from a push notification with ?item=ham[,beef],
  // build a queue of all alerted items that appeared in the notification, then
  // auto-expand + scroll to the first one. The "Next sale item →" pill inside
  // the expanded card lets the user cycle through the rest.
  useEffect(() => {
    if (!highlightItem || !scanned || alerts.length === 0) return;
    const wanted = highlightItem
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    // Preserve the order the notification used, but only keep items that
    // actually have alert cards rendered right now.
    const alertNamesLower = new Set(
      alerts.map((a) => (a.grocery_item || "").toLowerCase())
    );
    const queue = [];
    for (const w of wanted) {
      if (!alertNamesLower.has(w)) continue;
      // Resolve to the actual cased grocery_item string from the alerts list
      const match = alerts.find(
        (a) => (a.grocery_item || "").toLowerCase() === w
      );
      if (match && !queue.includes(match.grocery_item)) {
        queue.push(match.grocery_item);
      }
    }
    if (queue.length === 0) return;

    setHighlightQueue(queue);
    setHighlightIndex(0);
    scrollAndFlash(queue[0]);
    onHighlightHandled();
  }, [highlightItem, scanned, alerts]); // eslint-disable-line

  const itemsWithDeals = alerts.length;
  const itemsWithoutDeals = groceryItems.length - itemsWithDeals;

  const handleSelectForList = (groceryName, product) => {
    replaceItemWithDeal(groceryName, product);
    setReplacedItems(prev => new Set([...prev, groceryName]));
    setSelectedProduct(null);
    setSelectedGroceryItem(null);
    // Record positive feedback for AI learning
    sendMatchFeedback(groceryName, product.name, product.store, "accepted");
  };

  // ── AI Match Learning ─────────────────────────────────────────────────
  const [dismissedMatches, setDismissedMatches] = useState(new Set());

  const sendMatchFeedback = async (groceryItem, productName, store, action) => {
    try {
      await fetch(`${BACKEND_URL}/api/matching/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grocery_item: groceryItem,
          product_name: productName,
          store: store || "",
          action,
        }),
      });
    } catch (e) { /* silent */ }
  };

  const handleRejectMatch = (groceryItem, product, matchKey) => {
    sendMatchFeedback(groceryItem, product.name, product.store, "rejected");
    setDismissedMatches(prev => new Set([...prev, matchKey]));
  };

  const handleSmartAutoPick = () => {
    const replacements = [];
    for (const alert of alerts) {
      if (replacedItems.has(alert.grocery_item)) continue;
      if (!alert.cheapest || alert.cheapest.price == null) continue;
      replacements.push({ genericName: alert.grocery_item, dealProduct: alert.cheapest });
    }
    if (replacements.length === 0) return;

    batchReplaceItemsWithDeals(replacements);
    setReplacedItems(prev => {
      const newSet = new Set(prev);
      for (const r of replacements) newSet.add(r.genericName);
      return newSet;
    });
  };

  const openProductDetail = (groceryName, product) => {
    setSelectedGroceryItem(groceryName);
    setSelectedProduct(product);
  };

  // Group matches by store for comparison view
  const getStoreComparison = (matches) => {
    const byStore = {};
    for (const m of matches) {
      if (!byStore[m.store] || (m.price != null && (byStore[m.store].price == null || m.price < byStore[m.store].price))) {
        byStore[m.store] = m;
      }
    }
    return Object.values(byStore).sort((a, b) => (a.price ?? 9999) - (b.price ?? 9999));
  };

  return (
    <div className="space-y-3" data-testid="deal-alerts-tab">
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-amber-600" />
            <span className="text-xs text-amber-800">
              Compares your grocery list across <strong>all local stores</strong> to find the best prices
            </span>
          </div>
        </CardContent>
      </Card>

      <Button
        className="w-full bg-amber-500 hover:bg-amber-600 text-white"
        onClick={scanForDeals}
        disabled={loading || groceryItems.length === 0}
        data-testid="scan-deals-btn"
      >
        {loading ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Scanning {groceryItems.length} items...</>
        ) : (
          <><Wallet className="h-4 w-4 mr-2" />
            {scanned ? "Re-scan for Deals" : `Find Deals for ${groceryItems.length} Items`}
          </>
        )}
      </Button>

      {scanned && !loading && (
        <div className="flex gap-2">
          <Card className="flex-1 bg-green-50 border-green-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{itemsWithDeals}</p>
              <p className="text-xs text-green-600">items on sale</p>
            </CardContent>
          </Card>
          <Card className="flex-1 bg-orange-50 border-orange-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-orange-700">{totalMatches}</p>
              <p className="text-xs text-orange-600">total deals found</p>
            </CardContent>
          </Card>
          {itemsWithoutDeals > 0 && (
            <Card className="flex-1 bg-gray-50 border-gray-200">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-gray-500">{itemsWithoutDeals}</p>
                <p className="text-xs text-gray-500">no deals</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Smart Auto-Pick Button */}
      {scanned && !loading && alerts.length > 0 && (
        <Button
          className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold shadow-md"
          onClick={handleSmartAutoPick}
          disabled={replacedItems.size >= alerts.length}
          data-testid="smart-auto-pick-btn"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {replacedItems.size >= alerts.length
            ? "All Items Auto-Picked!"
            : `Smart Auto-Pick — Cheapest for All ${alerts.length} Items`
          }
        </Button>
      )}

      {alerts.map((alert) => {
        const isExpanded = expandedItem === alert.grocery_item;
        const cheapest = alert.cheapest;
        const storeComparison = isExpanded ? getStoreComparison(alert.matches) : [];
        const maxPrice = storeComparison.length > 0 ? Math.max(...storeComparison.map(s => s.price ?? 0)) : 1;

        return (
          <Card
            key={alert.grocery_item}
            ref={(el) => { if (el) alertCardRefs.current[alert.grocery_item] = el; }}
            className={`bg-white transition-all duration-500 ${
              flashItem === alert.grocery_item
                ? "ring-2 ring-green-400 shadow-lg shadow-green-200 scale-[1.01]"
                : ""
            }`}
            data-testid={`alert-${alert.grocery_item}`}
          >
            <CardContent className="p-0">
              <div
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedItem(isExpanded ? null : alert.grocery_item)}
                data-testid={`alert-header-${alert.grocery_item}`}
              >
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Tag className="h-4 w-4 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm text-gray-900 capitalize">{alert.grocery_item}</h4>
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      {alert.match_count} deal{alert.match_count > 1 ? "s" : ""}
                    </Badge>
                    {replacedItems.has(alert.grocery_item) && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs" data-testid={`replaced-badge-${alert.grocery_item}`}>
                        <CheckCircle2 className="h-3 w-3 mr-0.5" /> Selected
                      </Badge>
                    )}
                  </div>
                  {cheapest && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Best: <span className="font-bold text-green-600">${cheapest.price?.toFixed(2)}</span>
                      {" "}at <span className="font-medium">{cheapest.store}</span>
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100">
                  {/* "Next sale item →" pill — only shown when this card is part
                      of a multi-item deep-link queue. One tap advances to the
                      next alerted grocery item and scrolls to it. */}
                  {highlightQueue.length > 1 && highlightQueue.includes(alert.grocery_item) && (
                    <div className="px-3 pt-3">
                      <button
                        onClick={handleNextHighlight}
                        className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-xs font-semibold shadow-sm active:scale-[0.99] transition-all"
                        data-testid="next-highlight-btn"
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <Sparkles className="h-3.5 w-3.5" />
                          Next sale item
                          <span className="bg-white/20 rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                            {highlightIndex + 1}/{highlightQueue.length}
                          </span>
                        </span>
                        <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
                      </button>
                    </div>
                  )}

                  {/* Store-by-store price comparison bars */}
                  <div className="px-3 pt-3 pb-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      <Store className="h-3 w-3 inline mr-1" />
                      Price Comparison — {storeComparison.length} Store{storeComparison.length > 1 ? "s" : ""}
                    </p>
                    <div className="space-y-1.5">
                      {storeComparison.map((s, j) => {
                        const isBest = cheapest && s.price === cheapest.price && s.store === cheapest.store;
                        const barWidth = s.price != null ? Math.max(25, (s.price / maxPrice) * 100) : 30;
                        return (
                          <div
                            key={`${s.store}-${j}`}
                            className="flex items-center gap-2 group"
                            data-testid={`compare-store-${j}`}
                          >
                            <span className="text-xs text-gray-600 w-24 truncate flex-shrink-0 font-medium">{s.store}</span>
                            <div
                              className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden relative cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => openProductDetail(alert.grocery_item, s)}
                            >
                              <div
                                className={`h-full rounded-full flex items-center pl-2 transition-all ${isBest ? "bg-green-200" : "bg-gray-200"}`}
                                style={{ width: `${barWidth}%` }}
                              >
                                <span className={`text-xs font-bold ${isBest ? "text-green-800" : "text-gray-700"}`}>
                                  {s.price != null ? `$${s.price.toFixed(2)}` : "See store"}
                                </span>
                              </div>
                              {isBest && <CheckCircle2 className="h-3.5 w-3.5 text-green-600 absolute right-1.5 top-1" />}
                            </div>
                            {s.price != null && (
                              <button
                                className="flex-shrink-0 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold px-2 py-1 rounded-md flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => { e.stopPropagation(); handleSelectForList(alert.grocery_item, s); }}
                                data-testid={`use-store-${j}`}
                              >
                                <ShoppingBag className="h-3 w-3" /> Use
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* All matching products — clickable for detail view */}
                  <div className="px-3 pb-3 pt-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      <Tag className="h-3 w-3 inline mr-1" />
                      All Matching Products
                    </p>
                    <div className="space-y-1">
                      {alert.matches.map((m, i) => {
                        const isBest = cheapest && m.price === cheapest.price && m.store === cheapest.store;
                        const matchKey = `${alert.grocery_item}::${m.name}::${m.store}`;
                        if (dismissedMatches.has(matchKey)) return null;
                        return (
                          <div
                            key={`${m.store}-${m.name}-${i}`}
                            className={`flex items-center gap-2 p-2 rounded-lg group transition-colors ${
                              isBest ? "bg-green-50 border border-green-200 hover:bg-green-100" : "bg-gray-50 hover:bg-gray-100"
                            }`}
                            data-testid={`deal-match-${i}`}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer" onClick={() => openProductDetail(alert.grocery_item, m)}>
                              <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                                <Tag className="h-3 w-3 text-gray-300" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-800 truncate">{m.name}</p>
                                <p className="text-xs text-gray-500">{m.store}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {m.price != null ? (
                                <span className={`text-sm font-bold ${isBest ? "text-green-600" : "text-gray-800"}`}>
                                  ${m.price.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">See store</span>
                              )}
                              {isBest && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                              <button
                                className="text-gray-300 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 p-0.5"
                                onClick={(e) => { e.stopPropagation(); handleRejectMatch(alert.grocery_item, m, matchKey); }}
                                title="Not a match"
                                data-testid={`reject-match-${i}`}
                              >
                                <ThumbsDown className="h-3 w-3" />
                              </button>
                              {m.price != null && (
                                <button
                                  className="bg-green-500 hover:bg-green-600 text-white text-xs font-semibold px-2 py-1 rounded-md flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                                  onClick={(e) => { e.stopPropagation(); handleSelectForList(alert.grocery_item, m); }}
                                  data-testid={`use-match-${i}`}
                                >
                                  <ShoppingBag className="h-3 w-3" /> Use
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Smart Alternatives — cheaper substitute products from
                      other stores (e.g. chicken thighs when you searched
                      "chicken breast"). Only shown when strictly cheaper. */}
                  {alert.smart_alternatives && alert.smart_alternatives.length > 0 && (
                    <div className="px-3 pb-3 pt-2 border-t border-dashed border-amber-300" data-testid={`gls-smart-alts-${alert.grocery_item}`}>
                      <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2 flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-amber-600" />
                        Smart Alternatives
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] px-1.5 py-0 h-4 ml-0.5">
                          Cheaper substitutes
                        </Badge>
                      </p>
                      <div className="space-y-1.5">
                        {alert.smart_alternatives.map((alt, k) => (
                          <div
                            key={`alt-${k}`}
                            className="flex items-center gap-2 bg-amber-50/60 rounded-lg px-2 py-2 border border-amber-100"
                            data-testid={`gls-smart-alt-${alert.grocery_item}-${k}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate" title={alt.name}>{alt.name}</p>
                              <p className="text-[10px] text-gray-500">at {alt.store}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-sm font-bold text-amber-700">${alt.price.toFixed(2)}</div>
                              {alt.savings_vs_original > 0 && (
                                <div className="text-[10px] text-green-600 font-medium">save ${alt.savings_vs_original.toFixed(2)}</div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectForList(alert.grocery_item, { name: alt.name, price: alt.price, store: alt.store });
                              }}
                              data-testid={`gls-smart-alt-add-${alert.grocery_item}-${k}`}
                            >
                              <ShoppingBag className="h-3 w-3 mr-1" /> Use
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {groceryItems.length === 0 && (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="p-8 text-center">
            <ShoppingCart className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">Your grocery list is empty</p>
            <p className="text-sm text-gray-400 mt-1">Add items to your list to find matching deals</p>
          </CardContent>
        </Card>
      )}

      {scanned && !loading && alerts.length === 0 && groceryItems.length > 0 && (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="p-8 text-center">
            <Wallet className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No matching deals this week</p>
            <p className="text-sm text-gray-400 mt-1">Check back when new price comparisons are released</p>
          </CardContent>
        </Card>
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => { setSelectedProduct(null); setSelectedGroceryItem(null); }}
          groceryItemName={selectedGroceryItem}
          onSelectForList={(product) => handleSelectForList(selectedGroceryItem, product)}
        />
      )}
    </div>
  );
};

// ── Price Compare Tab ───────────────────────────────────────────────────

const PriceCompareTab = ({ zip = "", radius = 10 }) => {
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalGroups, setTotalGroups] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [mode, setMode] = useState("all");
  const [freshness, setFreshness] = useState(null);
  const [detailProduct, setDetailProduct] = useState(null); // {store row + group}

  const { currentList, updateCurrentList } = useListContext();
  const { toast } = useToast();

  const addItemToList = ({ name, price, category, store }) => {
    if (!currentList) return;
    const newItem = {
      id: Date.now(),
      name,
      quantity: 1,
      completed: false,
      category: category || "Other",
      estimated_price: price ?? null,
      store: store || "",
      deal_selected: true,
    };
    updateCurrentList([newItem, ...(currentList.items || [])]);
    toast({ title: "Added to List", description: `${name} added to ${currentList.name}` });
  };

  const fetchData = async (storeMode) => {
    setLoading(true);
    try {
      const url = new URL(`${BACKEND_URL}/api/weekly-ads/price-compare`);
      url.searchParams.set("limit", "100");
      if (storeMode === "favorites") url.searchParams.set("favorites_only", "true");
      if (zip) url.searchParams.set("zip", zip);
      url.searchParams.set("radius", String(radius));
      const res = await fetch(url.toString());
      if (res.ok) {
        const data = await res.json();
        setComparisons(data.comparisons || []);
        setTotalGroups(data.total_groups || 0);
        setFreshness(data.data_freshness || null);
      }
    } catch (e) {
      console.error("Price compare fetch failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (isoStr) => {
    if (!isoStr) return "Unknown";
    try {
      const d = new Date(isoStr);
      const now = new Date();
      const diffMs = now - d;
      const diffHrs = Math.floor(diffMs / 3600000);
      if (diffHrs < 1) return "Just now";
      if (diffHrs < 24) return `${diffHrs}h ago`;
      const diffDays = Math.floor(diffHrs / 24);
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch { return "Unknown"; }
  };

  const isFreshnessStale = freshness?.newest_fetch
    ? (new Date() - new Date(freshness.newest_fetch)) > 7 * 24 * 3600000
    : false;

  useEffect(() => {
    fetchData(mode);
  }, [mode, zip, radius]); // eslint-disable-line

  const filtered = searchTerm
    ? comparisons.filter((c) =>
        c.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.category || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : comparisons;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 text-orange-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="price-compare-tab">
      {/* Store scope toggle */}
      <div className="flex gap-2">
        <Button
          variant={mode === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setMode("all")}
          className={`flex-1 text-xs ${mode === "all" ? "bg-green-600 hover:bg-green-700 text-white" : "border-gray-300 text-gray-600"}`}
          data-testid="compare-mode-all"
        >
          <Store className="h-3 w-3 mr-1" />
          All Local Stores
        </Button>
        <Button
          variant={mode === "favorites" ? "default" : "outline"}
          size="sm"
          onClick={() => setMode("favorites")}
          className={`flex-1 text-xs ${mode === "favorites" ? "bg-green-600 hover:bg-green-700 text-white" : "border-gray-300 text-gray-600"}`}
          data-testid="compare-mode-favorites"
        >
          <Heart className="h-3 w-3 mr-1" />
          My Stores Only
        </Button>
      </div>

      {/* Summary + freshness */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-green-600" />
              <span className="text-xs text-green-800">
                <strong>{totalGroups}</strong> products compared across {mode === "favorites" ? "your favorite" : "all local"} stores
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-1.5 pt-1.5 border-t border-green-200/50 flex-wrap">
            <div className="flex items-center gap-1 text-xs text-green-700" data-testid="data-source">
              <Info className="h-3 w-3" />
              <span>Source: <strong>Current Price Comparisons</strong></span>
            </div>
            <div className="flex items-center gap-1 text-xs" data-testid="data-freshness">
              <Clock className="h-3 w-3" />
              <span className={isFreshnessStale ? "text-amber-600 font-medium" : "text-green-600"}>
                Updated: {formatTimeAgo(freshness?.newest_fetch)}
              </span>
              {isFreshnessStale && (
                <Badge className="bg-amber-100 text-amber-700 text-xs ml-1">Stale</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      {comparisons.length > 0 && (
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search products..."
            className="pl-8 h-9 text-sm"
            data-testid="search-compare"
          />
        </div>
      )}

      {/* Text-only product comparison list */}
      {filtered.map((group, i) => {
        const isExpanded = expandedGroup === i;
        const topStores = isExpanded ? group.stores : group.stores.slice(0, 3);
        const alts = group.smart_alternatives || [];
        return (
          <Card key={i} className="bg-white" data-testid={`compare-${i}`}>
            <CardContent className="p-0">
              <div
                className="p-3 cursor-pointer"
                onClick={() => setExpandedGroup(isExpanded ? null : i)}
              >
                {/* Product header — text only */}
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm text-gray-900 leading-tight" data-testid={`compare-name-${i}`}>
                      {group.product_name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {group.category && (
                        <Badge className="bg-gray-100 text-gray-600 text-xs">{group.category}</Badge>
                      )}
                      <Badge className={`text-xs ${group.store_count >= 4 ? "bg-green-100 text-green-700" : group.store_count >= 3 ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                        {group.store_count} stores
                      </Badge>
                    </div>
                  </div>
                  {group.max_savings > 0 && (
                    <div className="bg-green-100 rounded-lg px-2.5 py-1.5 text-center flex-shrink-0">
                      <p className="text-xs text-green-600">save up to</p>
                      <p className="text-lg font-bold text-green-700">${group.max_savings.toFixed(2)}</p>
                      {group.savings_pct > 0 && (
                        <p className="text-xs text-green-500">{group.savings_pct}% off</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Text-only store rows: Store | Item | Quantity | Price */}
                <div className="border-t border-gray-100">
                  <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)_minmax(0,0.8fr)_auto] gap-2 px-2 py-1.5 text-[10px] uppercase tracking-wide text-gray-400 font-semibold border-b border-gray-50">
                    <div>Store</div>
                    <div>Item</div>
                    <div className="text-right">Qty</div>
                    <div className="text-right pr-1">Price</div>
                  </div>
                  {topStores.map((s, j) => {
                    const isCheapest = s.store === group.cheapest_store;
                    const isBestValue = group.has_unit_prices && group.cheapest_unit_store && s.store === group.cheapest_unit_store;
                    const highlight = isBestValue || isCheapest;
                    const qty = s.details?.size || s.size_raw || (s.unit_price && s.unit_price_label ? s.unit_price_label.replace(/^\/\s*/, "") : "—");
                    return (
                      <button
                        key={j}
                        className={`w-full grid grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)_minmax(0,0.8fr)_auto] gap-2 items-center px-2 py-2 border-b border-gray-50 last:border-b-0 text-left ${highlight ? "bg-green-50/60" : "hover:bg-gray-50"} transition-colors`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailProduct({ row: s, group });
                        }}
                        data-testid={`compare-row-${i}-${j}`}
                      >
                        <div className="text-xs text-gray-800 font-medium truncate" title={s.store}>
                          {s.store}
                        </div>
                        <div className="text-xs text-gray-600 truncate flex items-center gap-1" title={s.name}>
                          <span className="truncate">{s.name}</span>
                          <Info className="h-3 w-3 text-gray-300 flex-shrink-0" />
                        </div>
                        <div className="text-right text-xs text-gray-500 font-mono">
                          {qty}
                        </div>
                        <div className="text-right pr-1 flex items-center justify-end gap-1">
                          <span className={`text-sm font-bold ${isBestValue ? "text-emerald-700" : isCheapest ? "text-green-700" : "text-gray-700"}`}>
                            ${s.price.toFixed(2)}
                          </span>
                          {highlight && <CheckCircle2 className={`h-3.5 w-3.5 ${isBestValue ? "text-emerald-600" : "text-green-600"}`} />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {group.stores.length > 3 && !isExpanded && (
                  <p className="text-xs text-gray-400 mt-1.5 text-center">
                    +{group.stores.length - 3} more stores
                    <ChevronDown className="h-3 w-3 inline ml-0.5" />
                  </p>
                )}
                {isExpanded && group.stores.length > 3 && (
                  <p className="text-xs text-gray-400 mt-1.5 text-center">
                    <ChevronUp className="h-3 w-3 inline mr-0.5" />
                    Show less
                  </p>
                )}

                {/* Smart Alternatives — only shown when strictly cheaper substitutes exist */}
                {alts.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dashed border-amber-300" data-testid={`smart-alts-${i}`}>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Sparkles className="h-3.5 w-3.5 text-amber-600" />
                      <span className="text-xs font-bold uppercase tracking-wide text-amber-700">
                        Smart Alternatives
                      </span>
                      <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] px-1.5 py-0 h-4">
                        Cheaper substitutes
                      </Badge>
                    </div>
                    <div className="space-y-1.5">
                      {alts.map((alt, k) => (
                        <div
                          key={k}
                          className="flex items-center gap-2 bg-amber-50/60 rounded-lg px-2 py-2 border border-amber-100"
                          data-testid={`smart-alt-${i}-${k}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-800 truncate" title={alt.name}>
                              {alt.name}
                            </div>
                            <div className="text-[10px] text-gray-500">
                              at {alt.cheapest_store}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-sm font-bold text-amber-700">
                              ${alt.cheapest_price.toFixed(2)}
                            </div>
                            <div className="text-[10px] text-green-600 font-medium">
                              save ${alt.savings_vs_original.toFixed(2)}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              addItemToList({
                                name: alt.name,
                                price: alt.cheapest_price,
                                store: alt.cheapest_store,
                                category: group.category,
                              });
                            }}
                            data-testid={`smart-alt-add-${i}-${k}`}
                          >
                            <ShoppingCart className="h-3 w-3 mr-1" />
                            Add
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {comparisons.length === 0 && !loading && (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="p-8 text-center">
            <DollarSign className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">
              {mode === "favorites"
                ? "No comparisons for your favorite stores"
                : "No price comparisons available"}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {mode === "favorites"
                ? "Add at least 2 stores to your favorites to compare prices between them"
                : "Set your zip code and refresh price data to enable cross-store cost comparison"}
            </p>
            {mode === "favorites" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMode("all")}
                className="mt-3 text-xs"
                data-testid="switch-to-all"
              >
                View All Local Stores Instead
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {comparisons.length > 0 && filtered.length === 0 && searchTerm && (
        <Card>
          <CardContent className="p-6 text-center">
            <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No products match &quot;{searchTerm}&quot;</p>
          </CardContent>
        </Card>
      )}

      <TextOnlyProductInfoModal
        open={!!detailProduct}
        product={detailProduct?.row}
        store={detailProduct?.row?.store}
        storeContext={
          detailProduct?.row && detailProduct.group &&
          detailProduct.row.store === detailProduct.group.cheapest_store
            ? "Cheapest"
            : null
        }
        onClose={() => setDetailProduct(null)}
        onAdd={() => {
          if (!detailProduct?.row) return;
          addItemToList({
            name: detailProduct.row.name,
            price: detailProduct.row.price,
            store: detailProduct.row.store,
            category: detailProduct.group?.category,
          });
        }}
      />
    </div>
  );
};


// ── Budget Friendly Tab — sale scanner grouped by store ─────────────────

const _BUDGET_SPECIAL_HITS = new Set([
  "digital coupon", "coupon", "clearance", "roll back", "mark down",
  "price drop", "member price", "must-buy", "$1", "dollar items",
  "2 for", "7 for 7", "on sale this week", "weekend sale", "on sale", "sale",
]);

const BudgetKeywordChip = ({ hit }) => {
  const isSpecial = _BUDGET_SPECIAL_HITS.has(hit);
  return (
    <Badge className={`text-[10px] px-1.5 py-0 h-4 ${isSpecial ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-emerald-100 text-emerald-700 border-emerald-300"}`}>
      {hit}
    </Badge>
  );
};

const BudgetStoreGroup = ({ group, matched, expanded, onToggle, onAdd, onDealClick }) => {
  const visibleDeals = expanded ? group.deals : group.deals.slice(0, 4);
  return (
    <Card className={matched ? "border-emerald-200" : ""} data-testid={`budget-store-${group.store}`}>
      <CardContent className="p-0">
        <button
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
          onClick={onToggle}
          data-testid={`budget-store-toggle-${group.store}`}
        >
          <div className="flex items-center gap-2 text-left">
            <Store className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-bold text-gray-900">{group.store}</span>
            <Badge className={`text-xs ${matched ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
              {group.count} {group.count === 1 ? "deal" : "deals"}
            </Badge>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>
        <div className="border-t border-gray-100">
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,0.8fr)_auto_auto] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-400 font-semibold border-b border-gray-50">
            <div>Item</div>
            <div className="text-right">Qty</div>
            <div className="text-right pr-1">Price</div>
            <div />
          </div>
          {visibleDeals.map((d, i) => (
            <div
              key={i}
              role="button"
              tabIndex={0}
              className="grid grid-cols-[minmax(0,2fr)_minmax(0,0.8fr)_auto_auto] gap-2 items-center px-3 py-2 border-b border-gray-50 last:border-b-0 hover:bg-emerald-50/40 cursor-pointer"
              onClick={() => onDealClick && onDealClick({ ...d, store: group.store })}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && onDealClick) {
                  e.preventDefault();
                  onDealClick({ ...d, store: group.store });
                }
              }}
              data-testid={`budget-deal-${group.store}-${i}`}
            >
              <div className="min-w-0">
                <div className="text-xs text-gray-800 truncate flex items-center gap-1" title={d.name}>
                  <span className="truncate">{d.name}</span>
                  <Info className="h-3 w-3 text-gray-300 flex-shrink-0" />
                </div>
                <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                  <BudgetKeywordChip hit={d.keyword_hit} />
                </div>
              </div>
              <div className="text-right text-xs text-gray-500 font-mono truncate">{d.quantity || "—"}</div>
              <div className="text-right pr-1 text-sm font-bold text-gray-900">
                {d.price != null ? `$${d.price.toFixed(2)}` : "—"}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[11px]"
                onClick={(e) => { e.stopPropagation(); onAdd({ ...d, store: group.store }); }}
                data-testid={`budget-deal-add-${group.store}-${i}`}
              >
                <ShoppingCart className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
          ))}
          {group.deals.length > 4 && !expanded && (
            <button
              className="w-full text-xs text-emerald-600 hover:text-emerald-700 py-2 border-t border-gray-50"
              onClick={onToggle}
            >
              Show {group.deals.length - 4} more from {group.store}
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const BudgetFriendlyTab = ({ zip = "", radius = 10 }) => {
  const [data, setData] = useState({ matches_on_list: [], more_local_deals: [], count_on_list: 0, count_more: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedStore, setExpandedStore] = useState(null);
  const [detailDeal, setDetailDeal] = useState(null);
  const [segment, setSegment] = useState("on_list"); // "on_list" | "more" | "all"
  const { currentList, updateCurrentList } = useListContext();
  const { toast } = useToast();

  const uncheckedItems = React.useMemo(
    () => (currentList?.items || []).filter(i => !i.completed).map(i => i.name).filter(Boolean),
    [currentList]
  );
  const itemsParam = uncheckedItems.join(",");

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const url = new URL(`${BACKEND_URL}/api/weekly-ads/budget-friendly`);
        if (itemsParam) url.searchParams.set("items", itemsParam);
        if (zip) url.searchParams.set("zip", zip);
        url.searchParams.set("radius", String(radius));
        const res = await fetch(url.toString());
        if (res.ok) setData(await res.json());
      } catch (e) {
        console.error("Budget friendly fetch failed:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [itemsParam, zip, radius]);

  const addToList = (deal) => {
    if (!currentList) return;
    const newItem = {
      id: Date.now(),
      name: deal.name,
      quantity: 1,
      completed: false,
      category: "Other",
      estimated_price: deal.price ?? null,
      store: deal.store || "",
      deal_selected: true,
    };
    updateCurrentList([newItem, ...(currentList.items || [])]);
    toast({ title: "Added to List", description: `${deal.name} added to ${currentList.name}` });
  };

  const applySearch = (groups) => {
    if (!searchTerm) return groups;
    const q = searchTerm.toLowerCase();
    return groups
      .map(g => ({
        ...g,
        deals: g.deals.filter(d => d.name.toLowerCase().includes(q) || g.store.toLowerCase().includes(q)),
      }))
      .filter(g => g.deals.length > 0);
  };

  const onListGroups = applySearch(data.matches_on_list || []);
  const moreGroups = applySearch(data.more_local_deals || []);

  // Auto-select the segment that actually has results so users never land
  // on an empty view. Only re-runs when the data availability changes; keeps
  // user's manual selection sticky once they interact.
  const [segmentAutoSet, setSegmentAutoSet] = useState(false);
  useEffect(() => {
    if (segmentAutoSet || loading) return;
    if (data.count_on_list === 0 && data.count_more > 0) {
      setSegment("more");
    }
    setSegmentAutoSet(true);
  }, [loading, data.count_on_list, data.count_more, segmentAutoSet]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const KeywordChip = BudgetKeywordChip;
  const renderStoreGroup = (group, matched) => {
    const key = matched ? `m-${group.store}` : `x-${group.store}`;
    return (
      <BudgetStoreGroup
        key={key}
        group={group}
        matched={matched}
        expanded={expandedStore === key}
        onToggle={() => setExpandedStore(expandedStore === key ? null : key)}
        onAdd={addToList}
        onDealClick={setDetailDeal}
      />
    );
  };

  return (
    <div className="space-y-3" data-testid="budget-friendly-tab">
      {/* Header summary */}
      <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-bold text-emerald-800">Budget Friendly</span>
          </div>
          <p className="text-xs text-emerald-700">
            Current sale items under 15 dollars in your area — {data.count_on_list + data.count_more} deals across{" "}
            {(data.matches_on_list.length + data.more_local_deals.length)} stores
            {data.count_on_list > 0 && <> · <b>{data.count_on_list} match your grocery list</b></>}
          </p>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search deals or stores..."
          className="pl-8 h-9 text-sm"
          data-testid="budget-search"
        />
      </div>

      {/* Segmented toggle — quick switch between list-matching + more local deals */}
      {(onListGroups.length > 0 || moreGroups.length > 0) && (
        <div
          role="tablist"
          aria-label="Budget friendly filter"
          className="inline-flex w-full rounded-lg bg-gray-100 p-1"
          data-testid="budget-segment-toggle"
        >
          {[
            { key: "on_list", label: "On My List", count: data.count_on_list, disabled: onListGroups.length === 0 },
            { key: "more", label: "More Local", count: data.count_more, disabled: moreGroups.length === 0 },
            { key: "all", label: "All", count: data.count_on_list + data.count_more, disabled: false },
          ].map(({ key, label, count, disabled }) => {
            const active = segment === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={disabled}
                onClick={() => setSegment(key)}
                className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-all ${
                  active
                    ? "bg-white text-emerald-700 shadow-sm"
                    : disabled
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`budget-segment-${key}`}
              >
                {label}
                <span className={`ml-1 text-[10px] ${active ? "text-emerald-500" : "text-gray-400"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* On your list — prioritized */}
      {(segment === "on_list" || segment === "all") && onListGroups.length > 0 && (
        <div className="space-y-2" data-testid="budget-on-list-section">
          <div className="flex items-center gap-2 px-1">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            <h3 className="text-sm font-bold text-emerald-800 uppercase tracking-wide">
              On your grocery list
            </h3>
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
              {data.count_on_list} matched
            </Badge>
          </div>
          {onListGroups.map((g) => renderStoreGroup(g, true))}
        </div>
      )}

      {/* More local deals */}
      {(segment === "more" || segment === "all") && moreGroups.length > 0 && (
        <div className="space-y-2 pt-2" data-testid="budget-more-section">
          <div className="flex items-center gap-2 px-1">
            <Store className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
              More Local Deals
            </h3>
            <Badge className="bg-gray-100 text-gray-600 text-[10px]">
              {data.count_more} deals
            </Badge>
          </div>
          {moreGroups.map((g) => renderStoreGroup(g, false))}
        </div>
      )}

      {/* Segment-specific empty hint (data exists in the OTHER bucket) */}
      {segment === "on_list" && onListGroups.length === 0 && moreGroups.length > 0 && (
        <Card className="border-dashed border-2 border-emerald-100 bg-emerald-50/30">
          <CardContent className="p-6 text-center">
            <Sparkles className="h-8 w-8 text-emerald-300 mx-auto mb-2" />
            <p className="text-sm text-emerald-800 font-medium">
              No matches for your grocery list this week
            </p>
            <button
              onClick={() => setSegment("more")}
              className="mt-2 text-xs text-emerald-700 font-semibold underline hover:text-emerald-900"
              data-testid="budget-jump-to-more"
            >
              Browse {data.count_more} other local deals →
            </button>
          </CardContent>
        </Card>
      )}
      {segment === "more" && moreGroups.length === 0 && onListGroups.length > 0 && (
        <Card className="border-dashed border-2 border-gray-100 bg-gray-50">
          <CardContent className="p-6 text-center">
            <Store className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-700 font-medium">
              No additional deals nearby right now
            </p>
            <button
              onClick={() => setSegment("on_list")}
              className="mt-2 text-xs text-emerald-700 font-semibold underline hover:text-emerald-900"
              data-testid="budget-jump-to-on-list"
            >
              See {data.count_on_list} deals matching your list →
            </button>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {onListGroups.length === 0 && moreGroups.length === 0 && (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="p-8 text-center">
            <DollarSign className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No sale items nearby right now</p>
            <p className="text-sm text-gray-400 mt-1">
              We&apos;ll scan again as new price comparisons roll in for your zip.
            </p>
          </CardContent>
        </Card>
      )}

      <TextOnlyProductInfoModal
        open={!!detailDeal}
        product={detailDeal}
        store={detailDeal?.store}
        storeContext={detailDeal?.matched_item ? "On your list" : null}
        groceryContext={detailDeal?.matched_item || null}
        onClose={() => setDetailDeal(null)}
        onAdd={() => detailDeal && addToList(detailDeal)}
      />
    </div>
  );
};

// ── Deal Alert Banner (from push notification) ─────────────────────────

const DealAlertBanner = ({ alertId, onDismiss }) => {
  const [alertData, setAlertData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!alertId) return;
    const fetchAlert = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await fetch(`${BACKEND_URL}/api/push/deal-alert/${alertId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setAlertData(data);
        }
      } catch (e) {
        console.error("Failed to fetch deal alert:", e);
      }
      setLoading(false);
    };
    fetchAlert();
  }, [alertId]);

  if (loading) {
    return (
      <Card className="border-green-300 bg-green-50 animate-pulse">
        <CardContent className="p-4 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-green-600" />
          <span className="text-sm text-green-700">Loading your sale items...</span>
        </CardContent>
      </Card>
    );
  }

  if (!alertData || !alertData.deals?.length) {
    return null;
  }

  return (
    <Card className="border-green-300 bg-gradient-to-r from-green-50 to-emerald-50 shadow-lg" data-testid="deal-alert-banner">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
              <ShoppingCart className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-green-800" data-testid="deal-alert-title">{alertData.title}</h3>
              <p className="text-xs text-green-600">{alertData.deals.length} item{alertData.deals.length > 1 ? "s" : ""} from your grocery list</p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="p-1 hover:bg-green-100 rounded-full"
            data-testid="deal-alert-dismiss"
          >
            <X className="h-4 w-4 text-green-400" />
          </button>
        </div>

        <div className="space-y-2" data-testid="deal-alert-items">
          {alertData.deals.map((deal, i) => (
            <div
              key={i}
              className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-green-100"
              data-testid={`deal-alert-item-${i}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{deal.product || deal.item}</p>
                <p className="text-xs text-gray-500">
                  {deal.item !== deal.product && <span className="text-green-600">From list: {deal.item}</span>}
                </p>
              </div>
              <div className="text-right flex-shrink-0 ml-2">
                {deal.price != null && (
                  <p className="text-sm font-bold text-green-700">${deal.price.toFixed(2)}</p>
                )}
                {deal.store && (
                  <p className="text-xs text-gray-400">{deal.store}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {alertData.created_at && (
          <p className="text-xs text-green-500 text-right">
            <Clock className="h-3 w-3 inline mr-0.5" />
            {new Date(alertData.created_at).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
};


// ── Premium Lock Card (shown to free users on premium sub-tabs) ────────

const PremiumLockCard = ({ label }) => {
  const navigate = useNavigate();
  return (
    <Card className="border-dashed border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50" data-testid={`premium-lock-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-6 flex flex-col items-center text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <Lock className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <h3 className="text-base font-bold text-amber-900">{label} is a Premium feature</h3>
          <p className="text-sm text-amber-700 mt-1">
            Subscribe to unlock cross-store price comparison, Grocery List Saver, and personalized deal alerts.
          </p>
        </div>
        <Button
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
          onClick={() => navigate("/subscription")}
          data-testid="premium-lock-subscribe-btn"
        >
          <Crown className="h-4 w-4 mr-1.5" />
          Subscribe — $4.99/mo
        </Button>
        <p className="text-[11px] text-amber-600">Community Deals is always free — keep exploring!</p>
      </CardContent>
    </Card>
  );
};


// ── Main Deals Page ─────────────────────────────────────────────────────

const Deals = () => {
  const { hasPremiumAccess } = useAuth();
  const { allowed: weeklyAdsAllowed } = useWeeklyAdsAccess();
  const { isGuest, promptSignup } = useGuest();
  const { zip: dealZip, radius: dealRadius, setLocation, promptOpen: zipPromptOpen,
          setPromptOpen: setZipPromptOpen, dismissPrompt: dismissZipPrompt,
          openPrompt: openZipPrompt } = useDealLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dealParam = searchParams.get("deal");
  const dealAlertParam = searchParams.get("deal_alert");
  const itemParam = searchParams.get("item");
  const storeParam = searchParams.get("store");
  const tabParam = searchParams.get("tab");
  // Price Compare is now the primary/default tab (text-only, safe to show to
  // all premium users). Explicit `?tab=` overrides everything; deep-links to
  // Community Deals via `?deal=` take priority next.
  const validTabs = new Set(["compare", "community", "budget", "alerts"]);
  const defaultTab = tabParam && validTabs.has(tabParam)
    ? tabParam
    : dealParam
      ? "community"
      : dealAlertParam && hasPremiumAccess
        ? "alerts"
        : isGuest
          ? "alerts"   // Grocery List Saver is the default guest tab
          : itemParam && !hasPremiumAccess
            ? "community"
            : hasPremiumAccess
              ? "compare"
              : "community";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showDealAlert, setShowDealAlert] = useState(!!dealAlertParam && hasPremiumAccess);
  const [highlightItem, setHighlightItem] = useState(itemParam || "");
  const [highlightStore, setHighlightStore] = useState(storeParam || "");

  // Auto-switch to correct tab when arriving via push notification deep link
  useEffect(() => {
    if (dealParam) {
      setActiveTab("community");
    }
    if (dealAlertParam) {
      setActiveTab("alerts");
      setShowDealAlert(true);
    }
    if (itemParam) {
      setHighlightItem(itemParam);
    }
    if (storeParam) {
      setHighlightStore(storeParam);
    }
    // Item + store deep-link from Home tooltip — auto-route premium users
    // straight into the Weekly Ads sub-tab so the flyer can open.
    if (itemParam && !storeParam && !hasPremiumAccess) {
      setActiveTab("community");
    }
  }, [dealParam, dealAlertParam, itemParam, storeParam, hasPremiumAccess, weeklyAdsAllowed]);

  // Guard: if the partnership gate closes (or was never open) and a *signed-in*
  // user is on the Grocery List Saver tab, snap back to a permitted tab.
  // Guests are always allowed on `alerts` (Grocery List Saver is a core guest
  // feature) — never bounce them.
  useEffect(() => {
    if (isGuest) return;
    if (!weeklyAdsAllowed && activeTab === "alerts") {
      setActiveTab(hasPremiumAccess ? "compare" : "community");
    }
  }, [weeklyAdsAllowed, activeTab, hasPremiumAccess, isGuest]);

  // Clear the deal param after navigating
  const clearDealParam = () => {
    if (dealParam) {
      searchParams.delete("deal");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const dismissDealAlert = () => {
    setShowDealAlert(false);
    if (dealAlertParam) {
      searchParams.delete("deal_alert");
      setSearchParams(searchParams, { replace: true });
    }
    if (itemParam) {
      searchParams.delete("item");
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Clear the item highlight (consumed) so later renders don't re-scroll
  const clearHighlightItem = () => {
    setHighlightItem("");
    setHighlightStore("");
    let changed = false;
    if (searchParams.get("item")) { searchParams.delete("item"); changed = true; }
    if (searchParams.get("store")) { searchParams.delete("store"); changed = true; }
    if (changed) setSearchParams(searchParams, { replace: true });
  };

  const allTabs = [
    { key: "compare", label: "Price Compare", icon: ArrowDown, style: "default", guestAllowed: false },
    { key: "community", label: "Community Deals", icon: Users, style: "community", guestAllowed: true },
    { key: "budget", label: "Budget Friendly", icon: DollarSign, style: "default", guestAllowed: true },
    { key: "alerts", label: "Grocery List Saver", icon: Wallet, style: "default", guestAllowed: true },
  ];
  // Weekly Ads tab intentionally removed from UI — backend endpoints remain
  // intact for a future partnership. Price Compare + Grocery List Saver are
  // both text-only and legally defensible, so no Flipp-gate filter needed.
  const tabs = allTabs;

  const handleTabClick = (tab) => {
    // Guests can only enter tabs flagged guestAllowed; others open the trial CTA
    if (isGuest && !tab.guestAllowed) {
      promptSignup({
        headline: `Sign up to unlock ${tab.label}`,
        subhead: "Start your 14-day free All-Access trial. No card, cancel anytime.",
      });
      return;
    }
    setActiveTab(tab.key);
  };

  return (
    <div className="p-4 space-y-4" data-testid="deals-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Deals & Savings</h2>
          <p className="text-gray-500 text-sm">Price comparisons, deal alerts & meal-driven savings</p>
        </div>
        <button
            type="button"
            onClick={openZipPrompt}
            className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200"
            data-testid="change-location-btn"
          >
            <MapPin className="h-3 w-3" />
            <span data-testid="current-location-label">
              {dealZip ? `${dealZip} · ${dealRadius} mi` : "Set Location"}
            </span>
          </button>
      </div>

      {/* Guest ZIP prompt (auto on first visit, on-demand via Change Location) */}
      <GuestZipPrompt
        open={zipPromptOpen}
        initialZip={dealZip}
        initialRadius={dealRadius}
        isGuest={isGuest}
        onClose={dismissZipPrompt}
        onSave={async (z, r) => {
          const ok = await setLocation(z, r);
          if (ok) setZipPromptOpen(false);
          return ok;
        }}
      />

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          const isCommunity = tab.style === "community";
          // Community is always free. Everything else requires premium (or signup for guests).
          const locked = (isGuest && !tab.guestAllowed) || (!hasPremiumAccess && !isCommunity && !isGuest);

          return (
            <Button
              key={tab.key}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => handleTabClick(tab)}
              className={`whitespace-nowrap text-xs transition-all ${
                isActive && isCommunity
                  ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white border-0 shadow-md shadow-violet-200"
                  : isActive
                  ? "bg-orange-500 hover:bg-orange-600 text-white"
                  : isCommunity
                  ? "border-violet-300 text-violet-600 bg-violet-50 hover:bg-violet-100"
                  : locked
                  ? "border-amber-200 text-amber-700 bg-amber-50/50 hover:bg-amber-50"
                  : "border-gray-300 text-gray-600"
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <Icon className="h-3 w-3 mr-1" />
              {tab.label}
              {locked && <Lock className="h-2.5 w-2.5 ml-1 opacity-60" />}
            </Button>
          );
        })}
      </div>

      {/* Deal Alert Banner — shown when arriving from push notification */}
      {showDealAlert && dealAlertParam && activeTab === "alerts" && (
        <DealAlertBanner alertId={dealAlertParam} onDismiss={dismissDealAlert} />
      )}

      {activeTab === "community" && <CommunityDealsTab highlightDealId={dealParam} highlightItem={highlightItem} onDealViewed={clearDealParam} onHighlightHandled={clearHighlightItem} guestZip={dealZip} zipPromptOpen={zipPromptOpen} />}
      {activeTab === "budget" && (isGuest || hasPremiumAccess ? <BudgetFriendlyTab zip={dealZip} radius={dealRadius} /> : <PremiumLockCard label="Budget Friendly" />)}
      {activeTab === "alerts" && (isGuest || hasPremiumAccess ? <GroceryListSaverTab highlightItem={highlightItem} onHighlightHandled={clearHighlightItem} zip={dealZip} radius={dealRadius} /> : <PremiumLockCard label="Grocery List Saver" />)}
      {activeTab === "compare" && !isGuest && (hasPremiumAccess ? <PriceCompareTab zip={dealZip} radius={dealRadius} /> : <PremiumLockCard label="Price Compare" />)}
    </div>
  );
};

export default Deals;
